import { trackEvent } from '../utils/usage.js';
import { createStore } from 'vuex';
import { signInWithPopup, signOut, onAuthStateChanged } from 'firebase/auth';
import { settleSharedSignIn, markInteractiveSignIn, endSharedSignIn } from '@/utils/sharedSignIn';
import { auth, googleProvider, firebaseConfigured } from '@/firebase';
import { ensureSiteUserRecord, watchSiteUserStatus } from '@/store/siteAccess';
import {
  createRoom, joinRoom, leaveLobby, startRoom, bindRoom, submitPage, advanceRound,
  setReveal, sweepMyRooms, gameFromRoom, seatOf, stepDriver, setBotCount,
  revealPageTurner, recordWaiting,
} from '@/store/multiplayer';
import { botPageFor, botsAvailable } from '@/store/botBrain';
import { outcomeOf, fingerprintOf, recordFinishedGame } from '@/store/userStats';
import {
  PHASES, advanceReveal, revealBackwards,
} from '@/engine/game';
import {
  assignmentFor, hasSubmitted, waitingOn, everyoneDone, MIN_PLAYERS,
} from '@/engine/rounds';

let unbindRoomFn = null;
let unwatchStatusFn = null;
// Guards the once-per-step advance so this client doesn't fire it repeatedly
// while the write is in flight.
let advancing = false;

export default createStore({
  state: {
    firebaseConfigured,
    authReady: !firebaseConfigured,
    // Separate from authReady: Firebase Auth can answer before the
    // siteUsers watcher has, and the gate must not flash "waiting for
    // approval" at an approved player in that gap.
    siteUserReady: !firebaseConfigured,
    user: null,
    siteUser: null,

    roomId: null,
    roomMeta: null,
    round: null,
    pages: {},
    reveal: null,
    presence: {},
    roomError: null,

    // What this player is working on right now, before it's submitted.
    draft: null,
  },

  getters: {
    isApproved: (state) => state.siteUser?.status === 'approved',
    isAdmin: (state) => Boolean(state.siteUser?.isAdmin),
    isHost: (state) => Boolean(state.roomMeta && state.user && state.roomMeta.hostUid === state.user.uid),
    myUid: (state) => state.user?.uid || null,
    inLobby: (state) => state.roomMeta?.status === 'lobby',

    game: (state) => gameFromRoom(state.roomMeta, state.round, state.pages, state.reveal),
    mySeat: (state, getters) => (getters.game ? seatOf(getters.game, state.user?.uid) : -1),
    myAssignment: (state, getters) => (
      getters.game && getters.mySeat >= 0 && getters.game.phase === PHASES.PLAYING
        ? assignmentFor(getters.game, getters.mySeat)
        : null
    ),
    iHaveSubmitted: (state, getters) => (
      getters.game && getters.mySeat >= 0 ? hasSubmitted(getters.game, getters.mySeat) : false
    ),
    // Only counts people who are actually here - a closed laptop must not hold
    // the rest of the table hostage.
    stillWorking: (state, getters) => (
      getters.game ? waitingOn(getters.game, state.presence) : []
    ),
    everyoneDone: (state, getters) => (
      getters.game ? everyoneDone(getters.game, state.presence) : false
    ),
    isRevealing: (state, getters) => getters.game?.phase === PHASES.REVEAL,

    /**
     * Who turns the pages right now - see `revealPageTurner` for the rule and
     * the bug report behind it. A robot's book falls to the host.
     */
    pageTurnerUid: (state, getters) => (
      getters.isRevealing
        ? revealPageTurner(getters.game, state.presence, state.roomMeta?.hostUid)
        : null
    ),

    /** Whoever is holding the book right now, for the "waiting on" line. */
    revealDriverName: (state, getters) => {
      const holder = getters.game?.players.find((p) => p.id === getters.pageTurnerUid);
      return holder?.name || 'Somebody';
    },

    iDriveReveal: (state, getters) => (
      getters.isRevealing && getters.pageTurnerUid === getters.myUid
    ),

    seats: (state) => [...(state.roomMeta?.playerSlots || [])].sort((a, b) => a.seatIndex - b.seatIndex),
    humanSeats: (state, getters) => getters.seats.filter((s) => !s.isBot),
    botSeats: (state, getters) => getters.seats.filter((s) => s.isBot),
    botsAvailable: () => botsAvailable(),
    enoughPlayers: (state, getters) => getters.seats.length >= MIN_PLAYERS,
  },

  mutations: {
    setAuthReady: (s, v) => { s.authReady = v; },
    setUser: (s, v) => { s.user = v; },
    setSiteUser: (s, v) => { s.siteUser = v; s.siteUserReady = true; },
    setSiteUserReady: (s, v) => { s.siteUserReady = v; },
    setRoom: (s, { id, meta }) => { s.roomId = id; s.roomMeta = meta ?? null; },
    setRoomMeta: (s, v) => { s.roomMeta = v; },
    setRound: (s, v) => { s.round = v; },
    setPages: (s, v) => { s.pages = v || {}; },
    setReveal: (s, v) => { s.reveal = v; },
    setPresence: (s, v) => { s.presence = v || {}; },
    setRoomError: (s, v) => { s.roomError = v; },
    setDraft: (s, v) => { s.draft = v; },
  },

  actions: {
    // Lifetime stats, once per finished game. Safe to dispatch as often as
    // anything notices the reveal has started - the fingerprint transaction
    // in userStats.js is what makes it exactly-once, not the caller.
    recordMyStats({ state, getters }) {
      const outcome = outcomeOf({ game: getters.game, myId: getters.myUid });
      if (!outcome) return;
      recordFinishedGame({
        uid: getters.myUid,
        name: state.user?.displayName || state.user?.email || null,
        fingerprint: fingerprintOf({ roomId: state.roomId, game: getters.game }),
        outcome,
      });
    },

    initAuth({ commit }) {
      if (!firebaseConfigured || !auth) { commit('setAuthReady', true); return; }
      onAuthStateChanged(auth, async (user) => {
        if (await settleSharedSignIn(auth, user)) return; // see utils/sharedSignIn.js
        if (unwatchStatusFn) { unwatchStatusFn(); unwatchStatusFn = null; }
        // An anonymous session exists only so a signed-out bug report can
        // carry a token (utils/bugReports.js). It is NOT a player: treat it
        // as signed out, and never write it a siteUsers record.
        if (user?.isAnonymous) {
          commit('setUser', null);
          commit('setSiteUser', null);
          commit('setAuthReady', true);
          return;
        }
        commit('setUser', user ? { uid: user.uid, displayName: user.displayName, email: user.email } : null);
        if (user) {
          commit('setSiteUserReady', false);
          await ensureSiteUserRecord(user);
          unwatchStatusFn = watchSiteUserStatus(user.uid, (su) => commit('setSiteUser', su));
        } else {
          commit('setSiteUser', null);
        }
        commit('setAuthReady', true);
      });
    },

    async signIn({ commit }) {
      if (!auth) return;
      try { markInteractiveSignIn(); await signInWithPopup(auth, googleProvider); } catch (e) { commit('setRoomError', e.message); }
    },

    async signOutOfSite({ commit, dispatch }) {
      await dispatch('leaveRoom');
      if (auth) { await endSharedSignIn(); await signOut(auth); }
      commit('setUser', null);
      commit('setSiteUser', null);
    },

    sweepRooms({ state }) {
      if (!state.user) return;
      sweepMyRooms({ user: state.user }).catch(() => {});
    },

    async hostRoom({ commit, state, dispatch }, { async: asyncRoom = false } = {}) {
      trackEvent('room-hosted');
      commit('setRoomError', null);
      try {
        const { gameId } = await createRoom({ user: state.user, async: asyncRoom });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    async joinRoomByCode({ commit, state, dispatch }, code) {
      trackEvent('room-joined');
      commit('setRoomError', null);
      try {
        const { gameId } = await joinRoom({ user: state.user, code });
        dispatch('enterRoom', gameId);
      } catch (e) { commit('setRoomError', e.message); }
    },

    enterRoom({ commit, state, dispatch }, gameId) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      commit('setRoom', { id: gameId, meta: null });
      commit('setRound', null);
      commit('setPages', {});
      commit('setReveal', null);
      commit('setDraft', null);

      unbindRoomFn = bindRoom({
        gameId,
        user: state.user,
        onMeta: (meta) => { commit('setRoomMeta', meta); if (meta === null) dispatch('leaveRoom'); },
        onRound: (round) => {
          const changedStep = round?.step !== state.round?.step;
          commit('setRound', round);
          // A new step means a blank page - never carry a draft across.
          if (changedStep) commit('setDraft', null);
          dispatch('runBots');
          dispatch('maybeAdvance');
        },
        onPages: (pages) => { commit('setPages', pages); dispatch('maybeAdvance'); },
        onReveal: (reveal) => commit('setReveal', reveal),
        onPresence: (presence) => {
          commit('setPresence', presence);
          // Presence decides who drives, so a change can make this client
          // newly responsible for the bots.
          dispatch('runBots');
          dispatch('maybeAdvance');
        },
      });
    },

    async leaveRoom({ commit, state }) {
      if (unbindRoomFn) { unbindRoomFn(); unbindRoomFn = null; }
      if (state.roomId && state.user) {
        try { await leaveLobby({ user: state.user, gameId: state.roomId }); } catch { /* best effort */ }
      }
      commit('setRoom', { id: null, meta: null });
      commit('setRound', null);
      commit('setPages', {});
      commit('setReveal', null);
      commit('setPresence', {});
      commit('setDraft', null);
    },

    async changeBotCount({ commit, state }, count) {
      try {
        await setBotCount({
          user: state.user, gameId: state.roomId, meta: state.roomMeta, count,
        });
      } catch (e) { commit('setRoomError', e.message); }
    },

    /**
     * Plays every bot's page for the current step.
     *
     * Only the driver does this - the same connected-lowest-seat rule that
     * advances the round - or every client would race to draw the same page
     * and burn money doing it. The write-once rule on the page node is the
     * backstop if two ever do.
     */
    async runBots({ state, getters, dispatch }) {
      const { game } = getters;
      if (!game || game.phase !== PHASES.PLAYING) return;
      if (stepDriver(game, state.presence) !== state.user?.uid) return;

      const owing = game.players.filter(
        (p) => p.isBot && !hasSubmitted(game, p.seat),
      );
      if (!owing.length) return;

      // Sequential, not parallel: a dozen simultaneous calls is a thundering
      // herd at the endpoint, and nobody is waiting on the bots individually.
      for (const bot of owing) {
        const step = game.step;
        // eslint-disable-next-line no-await-in-loop
        const assignment = assignmentFor(game, bot.seat);
        // eslint-disable-next-line no-await-in-loop
        const content = await botPageFor(assignment.kind, assignment.prompt);
        // The round may have moved on while Claude was thinking; drop it
        // rather than writing a page into the wrong step.
        if (!getters.game || getters.game.step !== step) return;
        // eslint-disable-next-line no-await-in-loop
        await submitPage({ gameId: state.roomId, game, seat: bot.seat, content }).catch(() => {});
        if (getters.game) recordWaiting({ gameId: state.roomId, game: getters.game, meta: state.roomMeta });
      }
      dispatch('maybeAdvance');
    },

    async deal({ commit, state }) {
      commit('setRoomError', null);
      try {
        await startRoom({ user: state.user, gameId: state.roomId, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    async submit({ commit, state, getters }, content) {
      const { game, mySeat, iHaveSubmitted } = getters;
      if (!game || mySeat < 0 || iHaveSubmitted) return;
      try {
        await submitPage({ gameId: state.roomId, game, seat: mySeat, content });
        commit('setDraft', null);
        if (getters.game) recordWaiting({ gameId: state.roomId, game: getters.game, meta: state.roomMeta });
      } catch (e) { commit('setRoomError', e.message); }
    },

    /**
     * Nudges the round along when the table is finished.
     *
     * Only the driver writes, and the write is a transaction that checks which
     * step it is advancing from - several clients can notice "everyone's done"
     * in the same instant, and without both guards the step would jump.
     */
    async maybeAdvance({ state, getters }) {
      const { game } = getters;
      if (!game || game.phase !== PHASES.PLAYING || advancing) return;
      if (!getters.everyoneDone) return;
      if (stepDriver(game, state.presence) !== state.user?.uid) return;
      advancing = true;
      try {
        await advanceRound({ gameId: state.roomId, fromStep: game.step, totalPages: game.pages });
        if (getters.game) recordWaiting({ gameId: state.roomId, game: getters.game, meta: state.roomMeta });
      } finally {
        advancing = false;
      }
    },

    async revealNext({ state, getters }) {
      if (!getters.iDriveReveal) return;
      await setReveal({ gameId: state.roomId, reveal: advanceReveal(getters.game).reveal });
    },

    async revealPrev({ state, getters }) {
      if (!getters.iDriveReveal) return;
      await setReveal({ gameId: state.roomId, reveal: revealBackwards(getters.game).reveal });
    },
  },
});
